import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { GlassCard, StatusBadge, IconChip } from '@/components/design';
import { Users, CalendarCheck, GraduationCap, ShieldAlert, AlertTriangle, Activity, ArrowRight, UserPlus, UserMinus, Briefcase, Lightbulb, Sparkles, ClipboardCheck, Star, Wallet } from 'lucide-react';

// People & Safety snapshot on the dashboard. Reads /data/hr-mrm/{cycle} — the SAME
// normalized object the PPT prints — so a value here is the value on the slide.
// Priority order. Tiles with a recorded figure show first; pending ones fill the remaining slots.
const TILES = [
  { key: 'employee_count', icon: Users, tone: 'indigo' },
  { key: 'attrition_pct', icon: Activity, tone: 'orange' },
  { key: 'attendance_pct', icon: CalendarCheck, tone: 'cyan' },
  { key: 'training_hours', icon: GraduationCap, tone: 'purple' },
  { key: 'accidents', icon: AlertTriangle, tone: 'red' },
  { key: 'lti', icon: ShieldAlert, tone: 'red' },
  { key: 'near_miss', icon: ShieldAlert, tone: 'orange' },
  { key: 'first_aid', icon: ShieldAlert, tone: 'blue' },
  { key: 'new_joiners', icon: UserPlus, tone: 'green' },
  { key: 'exits', icon: UserMinus, tone: 'red' },
  { key: 'open_vacancies', icon: Briefcase, tone: 'orange' },
  { key: 'suggestions_received', icon: Lightbulb, tone: 'purple' },
  { key: 'kaizens', icon: Sparkles, tone: 'green' },
  { key: 'kpi_coverage_pct', icon: ClipboardCheck, tone: 'blue' },
  { key: 'avg_kpi_score', icon: Star, tone: 'indigo' },
  { key: 'gross_salary_bill', icon: Wallet, tone: 'cyan' },
];
const MAX_TILES = 8;

export function useHrMrm(cycleId) {
  const [hr, setHr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!cycleId) { setHr(null); setLoading(false); return; }
    let active = true;
    setLoading(true);
    api.get(`/data/hr-mrm/${cycleId}`)
      .then((r) => { if (active) setHr(r.data || null); })
      .catch(() => { if (active) setHr(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cycleId]);
  return { hr, loading };
}

export function sourceLabel(src) {
  if (!src) return 'Data Pending';
  if (src.startsWith('hr_software') || src === 'derived:hr_software') return 'HR Software';
  if (src === 'hr_monthly' || src === 'derived:hr_monthly') return 'HR Monthly Sheet';
  if (src === 'none_recorded') return 'No incident recorded';
  if (src === 'manual') return 'Manual Input';
  if (src.startsWith('derived')) return 'Derived';
  return src;
}

export default function HrSnapshot({ cycleId, onOpen }) {
  const { hr, loading } = useHrMrm(cycleId);
  const fields = hr?.fields || {};
  const state = hr?.state || 'pending';
  const tag = hr?.source_tag || 'Manual Input';
  const loadedTiles = TILES.filter((t) => fields[t.key]?.state === 'loaded');
  const pendingTiles = TILES.filter((t) => fields[t.key]?.state !== 'loaded');
  const tiles = [...loadedTiles, ...pendingTiles].slice(0, MAX_TILES);
  const notRecorded = hr?.not_recorded || [];
  return (
    <GlassCard
      title="People & Safety"
      action={
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={hr?.hr_present ? 'uploaded' : state === 'unavailable' ? 'failed' : state === 'loaded' || state === 'zero' ? 'submitted' : 'pending'}
            label={hr?.hr_present ? `Source: ${tag}` : state === 'unavailable' ? 'HR upload failed' : state === 'pending' ? 'HR export awaited' : `Source: ${tag}`} />
          <button className="btn-ghost text-sm" onClick={onOpen} data-testid="btn-open-hr">Open <ArrowRight size={14} /></button>
        </div>
      }
    >
      {loading ? <div className="text-sm text-slate-500 py-4 text-center">Loading HR data…</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="hr-snapshot">
            {tiles.map((t) => {
              const f = fields[t.key] || {};
              const pending = f.state !== 'loaded';
              return (
                <div key={t.key} className="rounded-xl border border-[var(--aampl-border)] bg-white/80 p-3 flex flex-col gap-1.5 min-w-0" data-testid={`hr-tile-${t.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <IconChip tone={t.tone} icon={t.icon} size={30} iconSize={15} />
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 truncate">{sourceLabel(f.source)}</span>
                  </div>
                  <div className="kpi-label text-[10px] truncate" title={f.label}>{f.label || t.key}</div>
                  <div className={`font-extrabold tracking-tight ${pending ? 'text-sm text-[#C2410C]' : 'text-xl text-[var(--aampl-text)]'}`} data-testid={`hr-value-${t.key}`}>
                    {f.display || 'Data Pending'}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-slate-500 mt-3">{hr?.detail || 'No HR software export for this unit/period — upload it in the Upload Center (HR card).'}</div>
          {notRecorded.length > 0 && (
            <div className="text-[11px] text-[#C2410C] mt-1" data-testid="hr-not-recorded">
              Not in the HR software: {notRecorded.map((c) => ({ attendance: 'attendance', trainings: 'training' }[c] || c)).join(', ')} — fill the HR Monthly Sheet once a month (Upload Center → HR Monthly Sheet) and these show automatically.
            </div>
          )}
        </>
      )}
    </GlassCard>
  );
}
